import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

/** Session actions. The session itself is an HttpOnly cookie managed by the API; these calls just mirror it into state. */
function useSessionActions(setUser, setLoading, signedOut) {
  const adopt = useCallback((u) => { signedOut.current = false; setUser(u); return u; }, [setUser, signedOut]);

  // Logging out asks the server to clear the cookie.
  const logout = useCallback(() => {
    signedOut.current = true;
    setUser(null);
    return api.post("/auth/logout").catch(() => null);
  }, [setUser, signedOut]);

  const refresh = useCallback(async () => {
    try {
      return adopt((await api.get("/auth/me")).data);
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [adopt, setUser, setLoading]);

  const login = useCallback(async (email, password) => adopt((await api.post("/auth/login", { email, password })).data.user), [adopt]);
  const register = useCallback(async (payload) => adopt((await api.post("/auth/register", payload)).data.user), [adopt]);

  return useMemo(() => ({ login, register, logout, refresh }), [login, register, logout, refresh]);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // True only after an EXPLICIT sign-out (not after a session expiry). RequireAuth reads it synchronously so the
  // redirect to /auth does not remember the previous user's page as the post-login destination.
  const signedOut = useRef(false);
  const actions = useSessionActions(setUser, setLoading, signedOut);

  useEffect(() => {
    actions.refresh();
    const onUnauthorized = () => setUser(null);
    window.addEventListener("tamreer:unauthorized", onUnauthorized);
    return () => window.removeEventListener("tamreer:unauthorized", onUnauthorized);
  }, [actions]);

  // Actions are all stable callbacks; grouping them keeps the context value's dependency list to the two state slices + one actions slice.
  const value = useMemo(() => ({ user, loading, ...actions, setUser, signedOut }), [user, loading, actions]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
